import React from "react";
import {
  LayoutDashboard, Users, CheckSquare, BarChart3, Settings, BookUser,
  LogOut, Compass, UserCog, Home, Tags, ParkingCircle,
  Search as SearchIcon, Bookmark, FileSignature, XCircle, UserMinus,
  Hourglass, Archive, Scale, Mail,
} from "lucide-react";
import { NavLink, useLocation } from "react-router-dom";
import { t } from "@/i18n";
import { useAuth } from "@/lib/auth";
import useSyncHealth, { relTime, fmtDT } from "@/lib/useSyncHealth";

const baseItems = [
  { to: "/", key: "nav.dashboard", Icon: LayoutDashboard, testid: "nav-dashboard", end: true },
  { to: "/leads", key: "nav.leads", Icon: Users, testid: "nav-leads" },
  { to: "/particulieren", key: "nav.particulieren", Icon: Home, testid: "nav-particulieren" },
  { to: "/parkeren", key: "nav.parkeren", Icon: ParkingCircle, testid: "nav-parkeren" },
  { to: "/zoeken",    label: "Brief verstuurd", Icon: Mail, testid: "nav-brief-verstuurd" },
  { to: "/shortlist", label: "Shortlist", Icon: Bookmark,      testid: "nav-shortlist" },
  { to: "/getekend",  label: "Getekend",  Icon: FileSignature, testid: "nav-getekend" },
  { to: "/verloren",  label: "Verloren",  Icon: XCircle,       testid: "nav-verloren" },
  { to: "/ex-particulier", label: "Ex-particulier", Icon: UserMinus, testid: "nav-ex-particulier" },
  { to: "/follow-through", label: "Follow-through", Icon: Hourglass, testid: "nav-follow-through" },
  { to: "/archief", label: "Op rust", Icon: Archive, testid: "nav-archive" },
];

const trailingItems = [
  // Vergelijkingspunten staat tussen "Op rust" (laatste van baseItems) en Contacten —
  // visueel direct na het pipeline-blok, vóór de hulpmiddelen.
  { to: "/goals", key: "nav.goals", Icon: Scale, testid: "nav-goals" },
  { to: "/contacts", key: "nav.contacts", Icon: BookUser, testid: "nav-contacts" },
  { to: "/tasks", key: "nav.tasks", Icon: CheckSquare, testid: "nav-tasks" },
];

const adminItems = [
  { to: "/reports", key: "nav.reports", Icon: BarChart3, testid: "nav-reports" },
  { to: "/users", key: "nav.users", Icon: UserCog, testid: "nav-users" },
  { to: "/statuses", key: "nav.statuses", Icon: Tags, testid: "nav-statuses" },
  { to: "/settings", key: "nav.settings", Icon: Settings, testid: "nav-settings" },
];

const userOnlyItems = [
  { to: "/settings", key: "nav.settings", Icon: Settings, testid: "nav-settings" },
];

export default function Sidebar() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const isAdmin = user?.role === "super_admin" || user?.role === "tenant_admin";
  const adminTrail = [...trailingItems, ...(isAdmin ? adminItems : userOnlyItems)];
  return (
    <aside className="w-[240px] shrink-0 border-r border-[var(--border-soft)] bg-[var(--sand-50)] flex flex-col"
           data-testid="app-sidebar">
      <div className="px-5 pt-6 pb-4 flex items-center gap-2">
        <div className="w-9 h-9 rounded-xl bg-[var(--teal-600)] text-white grid place-items-center shadow-sm">
          <Compass size={18} strokeWidth={2} />
        </div>
        <div>
          <div className="font-display text-[17px] leading-none tracking-tight">FlowNavigator</div>
          <div className="text-[11px] text-[var(--ink-500)] mt-1">MVP · nl-BE</div>
        </div>
      </div>

      <nav className="px-3 mt-4 flex-1 space-y-1 overflow-y-auto">
        {baseItems.map(({ to, key, label, Icon, testid, end }) => (
          <NavLink
            key={to}
            to={to}
            end={!!end}
            data-testid={testid}
            className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
          >
            <Icon size={18} strokeWidth={1.75} />
            <span>{key ? t(key) : label}</span>
          </NavLink>
        ))}

        {adminTrail.map(({ to, key, Icon, testid, end }) => (
          <NavLink
            key={to}
            to={to}
            end={!!end}
            data-testid={testid}
            className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
          >
            <Icon size={18} strokeWidth={1.75} />
            <span>{t(key)}</span>
          </NavLink>
        ))}
      </nav>

      <div className="p-3 border-t border-[var(--border-soft)]">
        <SyncIndicator />
        <div className="flex items-center gap-3 px-2 py-2">
          <div className="w-9 h-9 rounded-full bg-[var(--teal-100)] text-[var(--teal-700)] grid place-items-center font-semibold text-sm">
            {user?.name?.split(" ").map(p => p[0]).slice(0,2).join("").toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-[var(--ink-900)] truncate" data-testid="current-user-name">
              {user?.name}
            </div>
            <div className="text-[11px] text-[var(--ink-500)] truncate">
              {t(`roles.${user?.role}`)}
            </div>
          </div>
          <button
            type="button"
            onClick={logout}
            data-testid="btn-logout"
            className="p-2 rounded-lg hover:bg-[var(--sand-100)] text-[var(--ink-500)] hover:text-[var(--danger)] transition-colors"
            title={t("common.logout")}
          >
            <LogOut size={16} />
          </button>
        </div>
      </div>
    </aside>
  );
}

function SyncIndicator() {
  const { health } = useSyncHealth();
  if (!health) return null;
  const isError = health.last_status === "error";
  const color = isError ? "bg-[#B43232]" : health.stale ? "bg-[#D08700]" : "bg-[#1F8B5F]";
  const label = isError ? "Sync-fout" : health.stale ? "Sync loopt achter" : "Sync up-to-date";
  return (
    <div className="flex items-center gap-2 px-2 pb-2 text-[11px] text-[var(--ink-500)]"
         data-testid="sidebar-sync-health"
         data-sync-state={isError ? "error" : health.stale ? "stale" : "ok"}
         title={`Laatste geslaagde sync: ${fmtDT(health.last_success_at)}`}>
      <span className={`w-2 h-2 rounded-full shrink-0 ${color}`} />
      <span className="truncate">{label} · {relTime(health.last_success_at)}</span>
    </div>
  );
}
